import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { increment, onValue, ref, update } from 'firebase/database';
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from '../../admin/sessionSlice';
import { getFirebaseDb, isFirebasePickStatsConfigured } from '../../config/firebase';
import { setBackButtonUrl } from '../uiSlice';
import { apiUrl } from '../../utils/apiUrl';
import {
  encodePickItemKey,
  makePickRoomId,
  mergePositionStats,
  slidesSnapshotToPositionStats,
} from '../../utils/pickStatsFirebase';
import { addReportToServer } from '../../functions/sendReport';
import { useMobileAppBrowserBackGuard } from '../../utils/useMobileAppBrowserBack';
import './game.css';

const S3 = process.env.REACT_APP_S3_PATH || '';

function s3Url(filename) {
  if (!filename) return '';
  return S3 + encodeURIComponent(filename).replace(/%2F/g, '/');
}
// import rules_img from '../../public/rules.png';
const DEMO_SLIDE_LIMIT = 3;

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '');
  return b || window.location.origin;
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Deterministic PRNG from a string seed (ensures all users in the same
// session see the exact same slide order).
function makeRng(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return function next() {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function seededShuffle(arr, rng) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function parseTimeToSeconds(t) {
  if (!t || typeof t !== 'string') return 0;
  const p = t.split(':').map((x) => parseInt(x, 10) || 0);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return 0;
}

function formatMmSs(sec) {
  const t = Math.max(0, Math.floor(sec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  return `${pad(m)}:${pad(s)}`;
}

/**
 * Build slide pairs from category_images_names.
 * Uses a seeded shuffle so every user in the same session (same seed)
 * gets the identical slide order — required for "% picked this" stats.
 */
function buildSlides(categoryImagesNames, max, seed) {
  const rng = makeRng(seed || 'default');
  const pairs = [];
  let cin = categoryImagesNames || {};
  if (typeof cin === 'string') {
    try { cin = JSON.parse(cin); } catch { cin = {}; }
  }
  if (typeof cin !== 'object' || Array.isArray(cin)) cin = {};

  // Sort category keys so iteration order is deterministic across browsers
  const sortedKeys = Object.keys(cin).sort();

  sortedKeys.forEach((category) => {
    const items = cin[category];
    if (!Array.isArray(items) || items.length < 2) return;
    for (let i = 0; i + 1 < items.length; i += 2) {
      pairs.push({
        left:  { ...items[i],     category },
        right: { ...items[i + 1], category },
      });
    }
  });

  return seededShuffle(pairs, rng).slice(0, max || 10);
}

// ── Choice content (image or text) ───────────────────────────────────────
function ItemImg({ item, textColor, textBg }) {
  const src = item?.image ? s3Url(item.image) : '';
  const contentType = item?.contentType || (src ? 'image' : 'text');
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [src]);

  if (contentType === 'text') {
    return (
      <div
        className="tot-item-text"
        style={{
          ...(textBg && { backgroundColor: textBg }),
          ...(textColor && { color: textColor }),
        }}
      >
        <span>{String(item?.name || '').slice(0, 30)}</span>
      </div>
    );
  }

  if (err || !src) {
    return (
      <div className="tot-item-placeholder">
        <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="6" fill="rgba(255,255,255,0.25)" />
          <path d="M6 24l7-9 5 6 4-4 7 7H6z" fill="rgba(255,255,255,0.65)" />
          <circle cx="11" cy="11" r="3" fill="rgba(255,255,255,0.65)" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="tot-item"
      draggable={false}
      referrerPolicy="no-referrer"
      onError={() => setErr(true)}
    />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Game() {
  const navigate    = useNavigate();
  const location = useLocation();
  const dispatch    = useDispatch();
  const { user }    = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken  = useSelector(selectAdminToken);
  const sessionId   = useSelector(selectSessionId);
  const organizationId = useSelector(selectOrganizationId);

  const useFirebasePicks = isFirebasePickStatsConfigured();

  const [statusChecked, setStatusChecked] = useState(false);
  const [alreadyDone, setAlreadyDone]     = useState(false);
  const [timerLabel, setTimerLabel]       = useState('00:00');
  const [isMobile, setIsMobile]           = useState(false);

  // Slide state
  const [slides, setSlides]                 = useState([]);
  const [currentSlide, setCurrentSlide]     = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [slideAnim, setSlideAnim]           = useState('idle');
  const [animating, setAnimating]           = useState(false);
  const [gameOver, setGameOver]             = useState(false);
  const [selectedSide, setSelectedSide]     = useState(null); // 'left' | 'right' | null
  const [positionStats, setPositionStats]   = useState({});   // { "slideIdx": { itemName: count } }
  const [showPct, setShowPct]               = useState(false);
  const [rulesOpen, setRulesOpen]           = useState(false);
  const slidesRef = useRef([]);

  const totalTimeRef         = useRef(0);
  const initialCountdownRef  = useRef(0);
  const minutesSecondRef     = useRef('00:00');
  const useTimeoutRef        = useRef(false);
  const submittingRef        = useRef(false);
  const saveDataRef          = useRef(() => {});
  const answersRef           = useRef([]);
  const reportIdRef          = useRef(user?.reportId ?? null);

  const themeKey  = themeData?.themename || '';
  const pickRoomId = useMemo(
    () => makePickRoomId(sessionId, organizationId, themeKey),
    [sessionId, organizationId, themeKey]
  );
  const useTimeout = themeData?.use_timeout === true || themeData?.use_timeout === 'true';
  const defaultTime = useMemo(() => (!themeData || !useTimeout) ? '00:00' : (themeData.timer || '00:00'), [themeData, useTimeout]);
  const numberOfSlides = Number(themeData?.number_of_slides) || 10;
  const isDemoUser = user?.source === 'DEMO';
  const effectiveSlideCount = isDemoUser ? DEMO_SLIDE_LIMIT : numberOfSlides;
  const isFromMobileApp = new URLSearchParams(location.search).get('fromMobileApp') === 'true';
  const mobileAppQuery = isFromMobileApp ? '?fromMobileApp=true' : '';
  useMobileAppBrowserBackGuard();

  const btnBgColor   = themeData?.button_backgroundcolor || '#8b4513';
  const btnTextColor = themeData?.button_color || '#ffffff';

  const resolvedUserName = useMemo(() => {
    if (!user) return '';
    const raw = (user.name || '').trim();
    if (raw && raw !== 'null' && raw !== 'null null') return raw;
    if (user.source === 'GUEST_USER') {
      return user.email ? user.email.split('@')[0] : (user.employeeId || user.userId || '');
    }
    return user.email ? user.email.split('@')[0] : (user.employeeId || user.userId || '');
  }, [user]);

  // Keep answersRef in sync
  useEffect(() => { answersRef.current = selectedAnswers; }, [selectedAnswers]);

  useEffect(() => {
    if (user?.reportId) {
      reportIdRef.current = user.reportId;
      return;
    }
    if (user?.userId && user?.sessionId) {
      try {
        const s = sessionStorage.getItem(`tot_report_${user.sessionId}_${user.userId}`);
        if (s) reportIdRef.current = s;
      } catch {
        /* ignore */
      }
    }
  }, [user?.reportId, user?.userId, user?.sessionId]);

  // Mobile detection (mirrors HTML script.js behaviour)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  useEffect(() => { dispatch(setBackButtonUrl('/login?&save=true')); }, [dispatch]);

  // Live pick counts from Firebase Realtime Database (not Firestore; no Firebase Auth)
  useEffect(() => {
    if (
      !useFirebasePicks
      || !sessionId
      || !organizationId
      || !themeKey
      || !pickRoomId
      || !statusChecked
      || gameOver
      || alreadyDone
    ) {
      return undefined;
    }
    const database = getFirebaseDb();
    if (!database) return undefined;
    const rtdbSlidesRef = ref(database, `pickRooms/${pickRoomId}/slides`);
    const unsubscribe = onValue(rtdbSlidesRef, (snap) => {
      // Never wipe MySQL/optimistic stats with an empty RTDB node
      if (!snap.exists()) return;
      const fromRtdb = slidesSnapshotToPositionStats(snap.val());
      setPositionStats((prev) => mergePositionStats(prev, fromRtdb));
    });
    return () => unsubscribe();
  }, [
    useFirebasePicks,
    pickRoomId,
    sessionId,
    organizationId,
    themeKey,
    statusChecked,
    gameOver,
    alreadyDone,
  ]);

  // Helper: fetch resume progress + session-wide positionStats (MySQL is source of truth)
  const fetchStats = useCallback(async () => {
    if (!adminToken || !user) return null;
    try {
      const resp = await fetch(apiUrl('/itemPickStats'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ userId: user.userId }),
      });
      const json = await resp.json();
      if (json.success) return json;
    } catch (e) {
      console.error('itemPickStats', e);
    }
    return null;
  }, [adminToken, user]);

  // Build slides + fetch stats + resume — single effect
  useEffect(() => {
    if (!themeData?.category_images_names || !user || !adminToken || themeStatus !== 'succeeded') return;
    let cancelled = false;

    const seed = themeData.themename || themeData.themeName || 'default';
    const built = buildSlides(themeData.category_images_names, effectiveSlideCount, seed);
    setSlides(built);
    slidesRef.current = built;
    setGameOver(false);
    setSlideAnim('idle');
    setAnimating(false);

    (async () => {
      const json = await fetchStats();
      if (cancelled) return;
      if (json) {
        // Always seed from MySQL so pair % works even if RTDB is empty/denied
        setPositionStats(json.positionStats || {});
        const userAnswers = json.userAnswers || [];
        if (userAnswers.length >= built.length) {
          setAlreadyDone(true);
          if (user?.source === 'DEMO') {
            window.location.replace(demoPlansUrl());
            return;
          }
          navigate(`/leaderboard${mobileAppQuery}`, { replace: true });
          return;
        }
        setSelectedAnswers(userAnswers);
        answersRef.current = userAnswers;
        setCurrentSlide(userAnswers.length);
      } else {
        setCurrentSlide(0);
        setSelectedAnswers([]);
        answersRef.current = [];
      }
      setStatusChecked(true);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    themeData?.category_images_names,
    effectiveSlideCount,
    themeData?.themename,
    user,
    adminToken,
    themeStatus,
    navigate,
    useFirebasePicks,
    mobileAppQuery,
  ]);

  // MySQL polling keeps pair % in sync across users (works even when RTDB fails)
  useEffect(() => {
    if (!statusChecked || gameOver || alreadyDone) return;
    const interval = setInterval(async () => {
      const json = await fetchStats();
      if (json?.positionStats) {
        setPositionStats((prev) => mergePositionStats(prev, json.positionStats));
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [statusChecked, gameOver, alreadyDone, fetchStats]);

  // ── Save to DB ─────────────────────────────────────────────────────────────
  const saveData = useCallback(async (answers) => {
    if (submittingRef.current) return;
    if (!user || !adminToken || !themeData) return;
    submittingRef.current = true;

    let timeToSave = minutesSecondRef.current;
    if (useTimeoutRef.current && initialCountdownRef.current > 0) {
      const elapsed = Math.min(initialCountdownRef.current,
        Math.max(0, initialCountdownRef.current - totalTimeRef.current));
      timeToSave = formatMmSs(elapsed);
    }

    const ans       = answers || [];
    const spotScore = ans.length;

    try {
      const resp = await fetch(apiUrl('/spotSubmit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId:       user.userId,
          email:        user.email || '',
          name:         resolvedUserName,
          time:         timeToSave,
          spot_ans:     JSON.stringify(ans),
          spot_score:   spotScore,
          total_score:  spotScore,
          currentTheme: themeData.themename || themeData.themeName,
        }),
      });

      const text    = await resp.text();
      let payload   = null;
      try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }

      if (!resp.ok) {
        const msg = payload?.error || text || 'Submit failed';
        await Swal.fire({ icon: 'error', title: 'Submit failed', text: msg });
        submittingRef.current = false;
        return;
      }

      if (payload?.demo === true || text?.trim() === 'demo' || user?.source === 'DEMO') {
        const msg = payload?.uniqueMsg || 'Thanks for playing (demo).';
        sessionStorage.setItem('spotThankYouMsg', msg);
        await Swal.fire({ icon: 'success', title: 'Thank you for playing.', text: 'Subscribe to any PLAN to play with your peers.' });
        window.location.href = demoPlansUrl();
        return;
      }

      navigate(`/leaderboard${mobileAppQuery}`, { replace: true });
    } catch (e) {
      console.error('spotSubmit', e);
      await Swal.fire({ icon: 'error', title: 'Network error', text: String(e.message) });
    }
    submittingRef.current = false;
  }, [user, adminToken, themeData, navigate, mobileAppQuery]);

  useEffect(() => { saveDataRef.current = saveData; }, [saveData]);

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!statusChecked || !themeData || alreadyDone) return;

    useTimeoutRef.current = useTimeout;
    const sec = parseTimeToSeconds(defaultTime);
    if (useTimeout) {
      initialCountdownRef.current = sec;
      totalTimeRef.current = sec;
    } else {
      initialCountdownRef.current = 0;
      totalTimeRef.current = 0;
    }

    const initialLabel = formatMmSs(useTimeout ? Math.max(0, sec) : 0);
    minutesSecondRef.current = initialLabel;
    setTimerLabel(initialLabel);

    const tick = () => {
      if (useTimeoutRef.current) totalTimeRef.current -= 1;
      else totalTimeRef.current += 1;
      const t = totalTimeRef.current;
      const display = formatMmSs(useTimeoutRef.current ? Math.max(0, t) : t);
      minutesSecondRef.current = display;
      setTimerLabel(display);
      if (useTimeoutRef.current && t <= 0) {
        clearInterval(id);
        saveDataRef.current(answersRef.current);
      }
    };

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusChecked, themeKey, alreadyDone, useTimeout, defaultTime]);

  // ── Choice handler (3-second highlight then slide) ─────────────────────────
  const HIGHLIGHT_MS = 2000;
  const SLIDE_MS     = 420;

  // Compute percentage for a slide position using position-based stats.
  // JSON keys are always strings, so we convert slideIdx to string.
  const getPairPct = useCallback((slideIdx, leftName, rightName) => {
    const key = String(slideIdx);
    const slideStats = positionStats[key] || {};
    const lc = slideStats[leftName] || 0;
    const rc = slideStats[rightName] || 0;
    const total = lc + rc;
    if (total === 0) return { left: 0, right: 0 };
    return {
      left:  Math.round((lc / total) * 100),
      right: Math.round((rc / total) * 100),
    };
  }, [positionStats]);

  const handleChoice = useCallback((chosenItem, side) => {
    if (animating || gameOver) return;

    const slideIdx = currentSlide;
    const newAnswers = [...answersRef.current, chosenItem.name];
    setSelectedAnswers(newAnswers);
    answersRef.current = newAnswers;

    // Optimistically update position stats so the badge shows immediately.
    // Use string key to match JSON format from the API.
    const key = String(slideIdx);
    setPositionStats((prev) => {
      const slideStats = { ...(prev[key] || {}) };
      slideStats[chosenItem.name] = (slideStats[chosenItem.name] || 0) + 1;
      return { ...prev, [key]: slideStats };
    });

    // RTDB: atomic increment for live % (dual-write with MySQL below)
    if (useFirebasePicks && pickRoomId && sessionId && organizationId && themeKey) {
      const database = getFirebaseDb();
      if (database) {
        const itemsRef = ref(database, `pickRooms/${pickRoomId}/slides/${slideIdx}/items`);
        update(itemsRef, { [encodePickItemKey(chosenItem.name)]: increment(1) }).catch((e) => {
          console.error('Firebase pick increment', e);
        });
      }
    }

    // Save pick to MySQL; always apply returned positionStats (session-wide pair counts)
    if (user && adminToken) {
      fetch(apiUrl('/saveSlideAnswer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId: user.userId,
          email:  user.email || '',
          name:   resolvedUserName,
          itemName: chosenItem.name,
          slideIndex: slideIdx,
          time: minutesSecondRef.current,
        }),
      })
        .then((r) => r.json())
        .then((json) => {
          if (json.success && json.positionStats) {
            setPositionStats(json.positionStats);
          }
        })
        .catch((e) => console.error('saveSlideAnswer', e));
    }

    addReportToServer(user, {
      points: newAnswers.length,
      reportTime: minutesSecondRef.current,
      answers: newAnswers,
      reportIdRef,
    });

    setSelectedSide(side);
    setShowPct(true);
    setAnimating(true);

    // Keep badge visible for the full highlight so MySQL/RTDB sync can update 50/50
    setTimeout(() => setShowPct(false), HIGHLIGHT_MS);

    const nextIndex = currentSlide + 1;

    setTimeout(() => {
      if (nextIndex >= slides.length) {
        setGameOver(true);
        setSelectedSide(null);
        setTimeout(() => saveDataRef.current(newAnswers), 300);
        return;
      }

      setSlideAnim('exit');
      setTimeout(() => {
        setSelectedSide(null);
        setCurrentSlide(nextIndex);
        setSlideAnim('enter');
        setTimeout(() => {
          setSlideAnim('idle');
          setAnimating(false);
        }, SLIDE_MS);
      }, SLIDE_MS);
    }, HIGHLIGHT_MS);
  }, [
    animating,
    gameOver,
    currentSlide,
    slides.length,
    user,
    adminToken,
    resolvedUserName,
    useFirebasePicks,
    pickRoomId,
    sessionId,
    organizationId,
    themeKey,
  ]);

  // ── Derived layout values ──────────────────────────────────────────────────
  const sceneBg  = isMobile
    ? (themeData?.background_mob  ? `url(${s3Url(themeData.background_mob)})`  : 'none')
    : (themeData?.background_desk ? `url(${s3Url(themeData.background_desk)})` : 'none');

  const boxBg = isMobile
    ? (themeData?.mobile_box  ? `url(${s3Url(themeData.mobile_box)})`  : 'none')
    : (themeData?.desktop_box ? `url(${s3Url(themeData.desktop_box)})` : 'none');

  const gameTitle = themeData?.landing_page_title || 'Taste Match';
  const slide     = slides[currentSlide];
  const pairPct   = slide ? getPairPct(currentSlide, slide.left.name, slide.right.name) : null;
  const itemTextColor = themeData?.item_text_color || '#1b265f';
  const itemTextBg = themeData?.item_text_backgroundcolor || '#ffffff';

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!user) { navigate('/login', { replace: true }); return null; }

  if (themeStatus !== 'succeeded' || !themeData || !statusChecked) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={`tot-scene${isMobile ? ' is-mobile' : ''}`}
      style={{ backgroundImage: sceneBg }}
      aria-label="Taste Match"
    >
      {/* Timer */}
      {useTimeout && (
        <div
          className="tot-timer"
          style={{ backgroundColor: btnBgColor, color: btnTextColor }}
        >
          {timerLabel}
        </div>
      )}

      {/* Title */}
      <h1
        className="tot-title"
        style={{ ...(themeData?.text_color && { color: themeData.text_color }) }}
        dangerouslySetInnerHTML={{ __html: gameTitle }}
      />

      <button
        type="button"
        className="tot-rules-btn"
       style={{ backgroundColor: 'transparent'}}
        onClick={() => setRulesOpen(true)}
      >
        {/* <i className="fa-solid fa-circle-info" aria-hidden="true" /> Rules */}
        <img src="/img/rules-img.png" alt="Rules" />
      </button>

      {/* Slide counter — below the cards (1-based question index, matches Slide N aria-label) */}
      {slides.length > 0 && (
        <div className="tot-slide-counter" style={{ ...(themeData?.text_color && { color: themeData.text_color }) }}>
          <span className="tot-slide-current">{currentSlide + 1}</span>
          <span className="tot-slide-sep">/</span>
          <span className="tot-slide-total">{slides.length}</span>
        </div>
      )}

      {/* Game over overlay */}
      {gameOver ? (
        <div className="tot-gameover">
          <div
            className="tot-gameover-msg"
            style={{ backgroundColor: btnBgColor, color: btnTextColor }}
          >
            Saving your answers…
          </div>
        </div>
      ) : slide ? (
        <section
          key={`slide-${currentSlide}`}
          className={`tot-choices tot-anim-${slideAnim}`}
          aria-label={`Slide ${currentSlide + 1}`}
        >
          {/* Left card */}
          <article
            className={`tot-choice-card${boxBg !== 'none' ? '' : ' tot-card-placeholder'}${selectedSide === 'left' ? ' tot-selected' : ''}${selectedSide === 'right' ? ' tot-not-selected' : ''}`}
            style={boxBg !== 'none' ? { backgroundImage: boxBg } : undefined}
            onClick={() => handleChoice(slide.left, 'left')}
            role="button"
            tabIndex={0}
            aria-label="Choose left"
            onKeyDown={(e) => e.key === 'Enter' && handleChoice(slide.left, 'left')}
          >
            <ItemImg item={slide.left} textColor={itemTextColor} textBg={itemTextBg} />
            {selectedSide && pairPct && (
              <div className={`tot-pct-badge${selectedSide === 'left' ? ' tot-pct-selected' : ''}${!showPct ? ' tot-pct-hide' : ''}`}>
                <span className="tot-pct-value">{pairPct.left}%</span>
                <span className="tot-pct-label">picked this</span>
              </div>
            )}
          </article>

          {/* Right card */}
          <article
            className={`tot-choice-card${boxBg !== 'none' ? '' : ' tot-card-placeholder'}${selectedSide === 'right' ? ' tot-selected' : ''}${selectedSide === 'left' ? ' tot-not-selected' : ''}`}
            style={boxBg !== 'none' ? { backgroundImage: boxBg } : undefined}
            onClick={() => handleChoice(slide.right, 'right')}
            role="button"
            tabIndex={0}
            aria-label="Choose right"
            onKeyDown={(e) => e.key === 'Enter' && handleChoice(slide.right, 'right')}
          >
            <ItemImg item={slide.right} textColor={itemTextColor} textBg={itemTextBg} />
            {selectedSide && pairPct && (
              <div className={`tot-pct-badge${selectedSide === 'right' ? ' tot-pct-selected' : ''}${!showPct ? ' tot-pct-hide' : ''}`}>
                <span className="tot-pct-value">{pairPct.right}%</span>
                <span className="tot-pct-label">picked this</span>
              </div>
            )}
          </article>
        </section>
      ) : (
        <div className="tot-loading">Loading items…</div>
      )}

      {rulesOpen && (
        <div className="tot-rules-modal-wrap" role="dialog" aria-modal="true" aria-label="Game rules">
          <div className="tot-rules-backdrop" onClick={() => setRulesOpen(false)} />
          <div className="tot-rules-modal" style={{overflow: 'visible'}}>
            <div className="tot-rules-modal-head">
             
              <button
                type="button"
                className="tot-rules-close"
                onClick={() => setRulesOpen(false)}
                aria-label="Close rules"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          <h2 className="tot-rules-modal-subtitle" style={{color: themeData?.rules_text_color}}>How to Play</h2>
            <ul className="tot-rules-list">
              {(Array.isArray(themeData?.rules) ? themeData.rules : []).map((rule, index) => (
                <li key={`${rule}-${index}`}>
                  <span style={{ color: themeData?.rules_text_color || themeData?.text_color || "#000000" }}>
                    {rule}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
